// =============================================================================
// Template Builder Page - DocuSign-style Drag & Drop Field Placement
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import esignService from '../../../services/esign.service';
import type {
  SignatureTemplate,
  SignatureTemplateField,
  CreateTemplateFieldInput,
  SignatureFieldType,
  SignatureDocType,
} from '../../../types/esign';
import { usePdfRenderer } from '../../../hooks/usePdfRenderer';
import { useDragResize } from '../../../hooks/useDragResize';
import FieldOverlay, {
  getSignerColor,
  SIGNER_COLORS,
  type FieldPosition,
} from './components/FieldOverlay';

// =============================================================================
// Constants
// =============================================================================

const DOC_TYPE_OPTIONS: { value: SignatureDocType; label: string }[] = [
  { value: 'NDA', label: 'NDA' },
  { value: 'SUBSCRIPTION', label: 'Subscription Agreement' },
  { value: 'SIDE_LETTER', label: 'Side Letter' },
  { value: 'PPM_ACKNOWLEDGMENT', label: 'PPM Acknowledgment' },
  { value: 'TAX_FORM', label: 'Tax Form' },
  { value: 'ACCREDITATION', label: 'Accreditation' },
  { value: 'OTHER', label: 'Other' },
];

const FIELD_TYPE_OPTIONS: { value: SignatureFieldType; label: string }[] = [
  { value: 'SIGNATURE', label: 'Signature' },
  { value: 'INITIALS', label: 'Initials' },
  { value: 'DATE_SIGNED', label: 'Date Signed' },
  { value: 'TEXT', label: 'Text' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'NAME', label: 'Full Name' },
  { value: 'EMAIL_FIELD', label: 'Email' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'TITLE_FIELD', label: 'Title' },
];

// Default sizes for each field type (percentage of container)
const DEFAULT_FIELD_SIZES: Record<SignatureFieldType, { w: number; h: number }> = {
  SIGNATURE: { w: 25, h: 5 },
  INITIALS: { w: 12, h: 4 },
  DATE_SIGNED: { w: 20, h: 3.5 },
  TEXT: { w: 30, h: 3.5 },
  CHECKBOX: { w: 3, h: 3 },
  NAME: { w: 25, h: 3.5 },
  EMAIL_FIELD: { w: 25, h: 3.5 },
  COMPANY: { w: 25, h: 3.5 },
  TITLE_FIELD: { w: 20, h: 3.5 },
};

// =============================================================================
// Main Component
// =============================================================================

export default function TemplateBuilderPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<SignatureTemplate | null>(null);
  const [fields, setFields] = useState<SignatureTemplateField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Field editor state
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savedIndicator, setSavedIndicator] = useState(false);

  // New field form
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldType, setNewFieldType] = useState<SignatureFieldType>('SIGNATURE');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [newFieldSigner, setNewFieldSigner] = useState(0);

  // Placing mode: user clicked "Place on PDF" and needs to click the document
  const [placingMode, setPlacingMode] = useState(false);

  // Editing field label inline
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  // Delete confirmation
  const [deletingFieldId, setDeletingFieldId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Create mode state
  const isCreateMode = !templateId;
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createDocType, setCreateDocType] = useState<SignatureDocType>('OTHER');
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);

  // Local positions during drag/resize (optimistic updates)
  const [localPositions, setLocalPositions] = useState<Map<string, FieldPosition>>(new Map());

  // Debounced batch save
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSavesRef = useRef<Map<string, FieldPosition>>(new Map());

  // ---------------------------------------------------------------------------
  // PDF Rendering
  // ---------------------------------------------------------------------------

  const getDocUrl = useCallback(async () => {
    if (!templateId) throw new Error('No template ID');
    return esignService.getTemplateDocumentUrl(templateId);
  }, [templateId]);

  const {
    pdfDoc,
    pdfLoading,
    canvasRef,
    containerRef,
    pageHeight,
  } = usePdfRenderer({
    getDocumentUrl: getDocUrl,
    currentPage,
    enabled: !!templateId,
  });

  // ---------------------------------------------------------------------------
  // Drag & Resize
  // ---------------------------------------------------------------------------

  const handlePositionChange = useCallback((fieldId: string, pos: FieldPosition) => {
    setLocalPositions((prev) => {
      const next = new Map(prev);
      next.set(fieldId, pos);
      return next;
    });
  }, []);

  const handlePositionCommit = useCallback((fieldId: string, pos: FieldPosition) => {
    // Update the field in local state immediately
    setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, ...pos } : f)));
    // Clear local override
    setLocalPositions((prev) => {
      const next = new Map(prev);
      next.delete(fieldId);
      return next;
    });

    // Queue for batch save
    pendingSavesRef.current.set(fieldId, pos);

    // Reset debounce timer
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const updates = Array.from(pendingSavesRef.current.entries()).map(
        ([fId, p]) => ({ fieldId: fId, ...p })
      );
      pendingSavesRef.current.clear();
      if (updates.length === 0 || !templateId) return;

      try {
        setSaving(true);
        const updatedFields = await esignService.batchUpdateFieldPositions(templateId, updates);
        setFields(updatedFields);
        setSavedIndicator(true);
        setTimeout(() => setSavedIndicator(false), 1500);
      } catch (err) {
        setError('Failed to save field positions');
      } finally {
        setSaving(false);
      }
    }, 500);
  }, [templateId]);

  const { interactionState, startDrag, startResize } = useDragResize({
    containerRef,
    onPositionChange: handlePositionChange,
    onPositionCommit: handlePositionCommit,
  });

  // Cleanup pending saves on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        const updates = Array.from(pendingSavesRef.current.entries()).map(
          ([fId, p]) => ({ fieldId: fId, ...p })
        );
        if (updates.length > 0 && templateId) {
          esignService.batchUpdateFieldPositions(templateId, updates).catch(() => {});
        }
      }
    };
  }, [templateId]);

  // Cancel placing mode on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPlacingMode(false);
        setSelectedField(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clear interaction state on page change
  useEffect(() => {
    setSelectedField(null);
    setLocalPositions(new Map());
  }, [currentPage]);

  // ---------------------------------------------------------------------------
  // Data Loading
  // ---------------------------------------------------------------------------

  const loadTemplate = useCallback(async () => {
    if (!templateId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [tmpl, flds] = await Promise.all([
        esignService.getTemplate(templateId),
        esignService.getTemplateFields(templateId),
      ]);
      setTemplate(tmpl);
      setFields(flds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || !createFile) return;
    setCreating(true);
    setError(null);
    try {
      const tmpl = await esignService.createTemplate(
        { name: createName.trim(), description: createDescription.trim() || undefined, documentType: createDocType },
        createFile,
      );
      navigate(`/admin/esign/templates/${tmpl.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  // Click on PDF to place a new field
  const handleContainerClick = async (e: React.PointerEvent) => {
    if (!placingMode || !containerRef.current || !templateId) return;

    const rect = containerRef.current.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
    const defaults = DEFAULT_FIELD_SIZES[newFieldType];

    // Center the field on click point, clamped to bounds
    const fieldX = Math.max(0, Math.min(xPercent - defaults.w / 2, 100 - defaults.w));
    const fieldY = Math.max(0, Math.min(yPercent - defaults.h / 2, 100 - defaults.h));

    setPlacingMode(false);
    setSaving(true);
    try {
      const field = await esignService.addTemplateField(templateId, {
        fieldType: newFieldType,
        label: newFieldLabel || FIELD_TYPE_OPTIONS.find((o) => o.value === newFieldType)?.label || newFieldType,
        isRequired: newFieldRequired,
        pageNumber: currentPage,
        xPercent: Math.round(fieldX * 10000) / 10000,
        yPercent: Math.round(fieldY * 10000) / 10000,
        widthPercent: defaults.w,
        heightPercent: defaults.h,
        signerIndex: newFieldSigner,
        tabOrder: fields.length,
      });
      setFields([...fields, field]);
      setSelectedField(field.id);
      setShowAddField(false);
      setNewFieldLabel('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add field');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!templateId || deleteConfirmText.toLowerCase() !== 'proceed') return;
    setSaving(true);
    try {
      await esignService.deleteTemplateField(templateId, fieldId);
      setFields(fields.filter((f) => f.id !== fieldId));
      setDeletingFieldId(null);
      setDeleteConfirmText('');
      if (selectedField === fieldId) setSelectedField(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete field');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = async (fieldId: string, updates: Partial<CreateTemplateFieldInput>) => {
    if (!templateId) return;
    setSaving(true);
    try {
      const updated = await esignService.updateTemplateField(templateId, fieldId, updates);
      setFields(fields.map((f) => (f.id === fieldId ? updated : f)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditLabel = (field: SignatureTemplateField) => {
    setEditingFieldId(field.id);
    setEditingLabel(field.label);
  };

  const handleSaveLabel = async () => {
    if (!editingFieldId || !editingLabel.trim()) return;
    await handleUpdateField(editingFieldId, { label: editingLabel.trim() });
    setEditingFieldId(null);
  };

  const pageFields = fields.filter((f) => f.pageNumber === currentPage);

  // ---------------------------------------------------------------------------
  // Loading / Error / Create Mode
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="space-y-6">
        <Link to="/admin/esign/templates" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Templates
        </Link>
        <div className="bg-white rounded-lg border p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900">{error}</h3>
          <button onClick={() => navigate('/admin/esign/templates')} className="mt-4 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800">
            Return to Templates
          </button>
        </div>
      </div>
    );
  }

  if (!template && !isCreateMode) return null;

  // Create mode: upload PDF form
  if (isCreateMode) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/esign/templates" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">New Template</h1>
            <p className="text-sm text-gray-500 mt-0.5">Upload a PDF and configure your signature template</p>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleCreateTemplate} className="space-y-6">
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Template Details</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="e.g. Subscription Agreement - Fund III" className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={createDescription} onChange={(e) => setCreateDescription(e.target.value)} placeholder="Optional description..." rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
              <select value={createDocType} onChange={(e) => setCreateDocType(e.target.value as SignatureDocType)} className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400">
                {DOC_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PDF Document *</label>
              <input type="file" accept=".pdf" onChange={(e) => setCreateFile(e.target.files?.[0] ?? null)} className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200" required />
              {createFile && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected: {createFile.name} ({(createFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <Link to="/admin/esign/templates" className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Cancel</Link>
            <button type="submit" disabled={creating || !createName.trim() || !createFile} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800 disabled:opacity-50">
              {creating ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Template Builder (main view)
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/esign/templates" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{template!.name}</h1>
            <p className="text-sm text-gray-500">
              {template!.pageCount} page{template!.pageCount !== 1 ? 's' : ''} &middot; {fields.length} field{fields.length !== 1 ? 's' : ''}
              {saving && <span className="ml-2 text-blue-500">Saving...</span>}
              {savedIndicator && !saving && <span className="ml-2 text-green-500">Saved</span>}
            </p>
          </div>
        </div>
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${template!.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          {template!.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 hover:text-red-700">Dismiss</button>
        </div>
      )}

      {/* Placing mode banner */}
      {placingMode && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <p className="text-sm text-blue-700 font-medium">
            Click anywhere on the document to place the <strong>{FIELD_TYPE_OPTIONS.find((o) => o.value === newFieldType)?.label}</strong> field
          </p>
          <button onClick={() => setPlacingMode(false)} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Cancel (Esc)</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* ================================================================= */}
        {/* PDF Preview Area                                                   */}
        {/* ================================================================= */}
        <div className="bg-white rounded-lg border">
          {/* Page navigation */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {template!.pageCount}
            </span>
            <div className="flex gap-1">
              {Array.from({ length: template!.pageCount }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1 text-xs font-medium rounded ${
                    currentPage === p ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Document area */}
          <div className="relative bg-gray-100 p-4 overflow-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
            <div
              ref={containerRef}
              className={`relative bg-white shadow-sm mx-auto select-none ${placingMode ? 'cursor-crosshair' : ''}`}
              style={{
                maxWidth: '612px',
                minHeight: pdfDoc ? pageHeight : 792,
              }}
              onPointerDown={placingMode ? handleContainerClick : undefined}
            >
              {/* PDF Canvas */}
              {pdfLoading ? (
                <div className="flex flex-col items-center justify-center" style={{ minHeight: 792 }}>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
                  <p className="mt-3 text-sm text-gray-400">Loading document...</p>
                </div>
              ) : pdfDoc ? (
                <canvas ref={canvasRef} className="w-full pointer-events-none" />
              ) : (
                <div className="flex items-center justify-center text-gray-400 text-sm" style={{ minHeight: 792 }}>
                  PDF Page {currentPage} Preview
                </div>
              )}

              {/* Field overlays */}
              {pageFields.map((field) => {
                const pos = localPositions.get(field.id) ?? {
                  xPercent: field.xPercent,
                  yPercent: field.yPercent,
                  widthPercent: field.widthPercent,
                  heightPercent: field.heightPercent,
                };
                return (
                  <FieldOverlay
                    key={field.id}
                    field={field}
                    position={pos}
                    isSelected={selectedField === field.id}
                    isDragging={interactionState?.fieldId === field.id && interactionState.type === 'dragging'}
                    onSelect={() => setSelectedField(field.id)}
                    onDragStart={(e) => startDrag(e, field.id, pos)}
                    onResizeStart={(e, handle) => startResize(e, field.id, pos, handle)}
                  />
                );
              })}

              {/* Placing mode visual indicator */}
              {placingMode && (
                <div className="absolute inset-0 border-2 border-blue-400 border-dashed rounded pointer-events-none z-40" />
              )}
            </div>
          </div>
        </div>

        {/* ================================================================= */}
        {/* Side Panel                                                         */}
        {/* ================================================================= */}
        <div className="space-y-4">
          {/* Signer selector legend */}
          <div className="bg-white rounded-lg border p-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Signers</h3>
            <div className="space-y-1">
              {SIGNER_COLORS.slice(0, Math.max(2, ...fields.map((f) => f.signerIndex + 1), newFieldSigner + 1)).map((color, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color.border }} />
                  <span className="text-gray-700">Signer {i + 1}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Add Field Button / Form */}
          {!showAddField ? (
            <button
              onClick={() => { setShowAddField(true); setSelectedField(null); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800"
            >
              <PlusIcon className="h-4 w-4" />
              Add Field
            </button>
          ) : (
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">New Field</h3>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value as SignatureFieldType)}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
                >
                  {FIELD_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                <input
                  type="text"
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  placeholder={FIELD_TYPE_OPTIONS.find((o) => o.value === newFieldType)?.label || 'Field label'}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Signer #</label>
                <select
                  value={newFieldSigner}
                  onChange={(e) => setNewFieldSigner(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-400"
                >
                  {[0, 1, 2, 3, 4, 5].map((i) => {
                    const c = getSignerColor(i);
                    return <option key={i} value={i}>Signer {i + 1} ({c.label})</option>;
                  })}
                </select>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newFieldRequired}
                  onChange={(e) => setNewFieldRequired(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Required
              </label>

              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAddField(false); setPlacingMode(false); }}
                  className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setPlacingMode(true)}
                  disabled={saving}
                  className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Place on PDF
                </button>
              </div>
            </div>
          )}

          {/* Selected Field Properties */}
          {selectedField && (() => {
            const field = fields.find((f) => f.id === selectedField);
            if (!field) return null;
            const pos = localPositions.get(field.id) ?? field;
            const color = getSignerColor(field.signerIndex);

            return (
              <div className="bg-white rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Field Properties</h3>
                  <button
                    onClick={() => {
                      setDeletingFieldId(field.id);
                      setDeleteConfirmText('');
                    }}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Delete field"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Label */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Label</label>
                  {editingFieldId === field.id ? (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={editingLabel}
                        onChange={(e) => setEditingLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(); if (e.key === 'Escape') setEditingFieldId(null); }}
                        className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400"
                        autoFocus
                      />
                      <button onClick={handleSaveLabel} className="px-2 py-1 text-xs text-white bg-gray-900 rounded">Save</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-900">{field.label}</span>
                      <button onClick={() => handleStartEditLabel(field)} className="p-0.5 text-gray-400 hover:text-gray-600">
                        <PencilSquareIcon className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Type & Signer */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                    <span className="text-sm text-gray-900">{FIELD_TYPE_OPTIONS.find((o) => o.value === field.fieldType)?.label}</span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Signer</label>
                    <div className="flex items-center gap-1">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color.border }} />
                      <select
                        value={field.signerIndex}
                        onChange={(e) => handleUpdateField(field.id, { signerIndex: parseInt(e.target.value) })}
                        className="text-sm text-gray-900 border-none p-0 bg-transparent focus:outline-none"
                      >
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <option key={i} value={i}>Signer {i + 1}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Required toggle */}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={field.isRequired}
                    onChange={(e) => handleUpdateField(field.id, { isRequired: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  Required
                </label>

                {/* Position info (read-only) */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 font-mono">
                    Pos: {pos.xPercent.toFixed(1)}%, {pos.yPercent.toFixed(1)}% &nbsp;|&nbsp; Size: {pos.widthPercent.toFixed(1)}% x {pos.heightPercent.toFixed(1)}%
                  </p>
                  <p className="text-[10px] text-gray-400">Page {field.pageNumber}</p>
                </div>
              </div>
            );
          })()}

          {/* Fields list for current page */}
          <div className="bg-white rounded-lg border">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Fields on Page {currentPage}</h3>
              <span className="text-xs text-gray-400">{pageFields.length}</span>
            </div>

            {pageFields.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No fields on this page
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {pageFields.map((field) => {
                  const color = getSignerColor(field.signerIndex);
                  return (
                    <div
                      key={field.id}
                      className={`px-3 py-2 cursor-pointer transition-colors flex items-center gap-2 ${
                        field.id === selectedField ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedField(field.id === selectedField ? null : field.id)}
                    >
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color.border }} />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium text-gray-900 truncate block">{field.label}</span>
                        <span className="text-[10px] text-gray-400">
                          {FIELD_TYPE_OPTIONS.find((o) => o.value === field.fieldType)?.label}
                          {field.isRequired && ' *'}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingFieldId(field.id);
                          setDeleteConfirmText('');
                        }}
                        className="p-1 text-gray-300 hover:text-red-500 rounded flex-shrink-0"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* All fields summary */}
          {fields.length > 0 && (
            <div className="bg-white rounded-lg border">
              <div className="px-4 py-3 border-b">
                <h3 className="text-sm font-semibold text-gray-900">All Fields ({fields.length})</h3>
              </div>
              <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {fields.map((field) => {
                  const color = getSignerColor(field.signerIndex);
                  return (
                    <div key={field.id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                      <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: color.border }} />
                      <span className="text-gray-700 truncate flex-1">{field.label}</span>
                      <span className="text-gray-400 flex-shrink-0">pg {field.pageNumber}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Field Confirmation Modal */}
      {deletingFieldId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setDeletingFieldId(null); setDeleteConfirmText(''); }} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">Delete this field?</p>
              <p className="text-sm text-red-600 mt-1">
                Deleting &quot;{fields.find((f) => f.id === deletingFieldId)?.label}&quot; cannot be undone.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                onClick={() => { setDeletingFieldId(null); setDeleteConfirmText(''); }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                onClick={() => handleDeleteField(deletingFieldId)}
                disabled={saving || deleteConfirmText.toLowerCase() !== 'proceed'}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
